import type { LocalePack } from './types';

/**
 * Non-English locale packs are split into their own webpack chunks so the main
 * bundle only carries English. A pack is fetched the first time its language is
 * requested and cached for the lifetime of the page.
 */
const LOCALE_LOADERS: Record<string, () => Promise<{ default: LocalePack }>> = {
  es: () => import(/* webpackChunkName: "locale-es" */ './es'),
  ar: () => import(/* webpackChunkName: "locale-ar" */ './ar'),
};

const loadedPacks = new Map<string, LocalePack>();
const pendingLoads = new Map<string, Promise<LocalePack | null>>();

export function normalizeLanguage(language?: string | null): string {
  return String(language || 'en')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

/** `es-MX` -> `['es-mx', 'es']` so a regional tag falls back to its base language. */
export function getLanguageCandidates(language?: string | null): string[] {
  const normalized = normalizeLanguage(language);
  const [base] = normalized.split('-');
  if (base && base !== normalized) return [normalized, base];
  return [normalized];
}

/** The locale-pack code (`es`, `ar`, ...) that serves `language`, or `null` for English/unknown. */
export function resolveLocalePackCode(language?: string | null): string | null {
  return getLanguageCandidates(language).find(code => code in LOCALE_LOADERS) ?? null;
}

export function getLoadedLocalePack(code: string | null | undefined): LocalePack | null {
  if (!code) return null;
  return loadedPacks.get(code) ?? null;
}

/** Register a pack synchronously (used by the loader and by tests / integrators). */
export function registerLocalePack(code: string, pack: LocalePack): void {
  loadedPacks.set(normalizeLanguage(code), pack);
}

export function loadLocalePack(language?: string | null): Promise<LocalePack | null> {
  const code = resolveLocalePackCode(language);
  if (!code) return Promise.resolve(null);
  const cached = loadedPacks.get(code);
  if (cached) return Promise.resolve(cached);
  const pending = pendingLoads.get(code);
  if (pending) return pending;
  const promise = LOCALE_LOADERS[code]()
    .then(mod => {
      loadedPacks.set(code, mod.default);
      return mod.default;
    })
    .finally(() => {
      pendingLoads.delete(code);
    });
  pendingLoads.set(code, promise);
  return promise;
}

/** @internal Clears the cache so tests can observe the English-first behaviour. */
export function __resetLocalePacksForTests(): void {
  loadedPacks.clear();
  pendingLoads.clear();
}
